import { NgClass, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProcessRequest } from '@compiere-ws/models/apiz-domains';
import {
  CompiereDataGridFilterModel,
  CompiereDataGridFilterType,
  CompiereDataGridRequestJSON,
  CompiereDataGridType,
  DataStore,
  DataStoreKey,
  CompiereDataFieldType,
  DataStoreRequest,
  DataStoreStatus,
} from '@compiere-ws/models/compiere-data-json';
import { ItemStateType } from '@compiere-ws/models/apiz-domains';
import { FieldStateService } from '@compiere-ws/services/field-state.service';
import {
  ToolbarProcess,
  ToolbarProcessField,
  ToolbarProcessSelectionState,
} from '@iupics-manager/models/toolbar-item';
import { RecentItem } from '@compiere-ws/models/compiere-recent-item';
import { CompiereProcessService } from '@compiere-ws/services/compiere-process/compiere-process.service';
import { ModalDisplayRequest, ModalDisplayRequester } from '@iupics-components/models/ui-request';
import { OperatorFilterType } from '@iupics-components/models/universal-filter';
import { ViewType } from '@iupics-components/models/view-type.enum';
import { InfoDialogType } from '@iupics-components/specific/window/info-dialog/info-dialog.component';
import SpecificWindowUiComponent, {
  SpecificGridQuery,
} from '@iupics-components/specific/window/specific-window-ui/specific-window-ui.component';
import GridTabInfinityScrollUiComponent, {
  GridTabInfinityScrollHeadlessUiComponent,
} from '@iupics-components/standard/grid/grid-tab-infinity-scroll-ui/grid-tab-infinity-scroll-ui.component';
import BladeUiComponent from '@iupics-components/standard/layouts/blade-ui/blade-ui.component';
import CardsUiComponent from '@iupics-components/standard/layouts/cards-ui/cards-ui.component';
import EditTabUiComponent from '@iupics-components/standard/layouts/edit-tab-ui/edit-tab-ui.component';
import EditViewUiComponent from '@iupics-components/standard/layouts/edit-view-ui/edit-view-ui.component';
import { EditViewUtils } from '@iupics-components/standard/layouts/edit-view-ui/utils/edit-view.utils';
import ModalUiComponent from '@iupics-components/standard/layouts/modal-ui/modal-ui.component';
import MenuBarUiComponent from '@iupics-components/standard/menu/menu-bar-ui/menu-bar-ui.component';
import { GridViewGetFormIDByTabID } from '@iupics-components/standard/menu/utils/menu.utils';
import { AppConfig } from '@iupics-config/app.config.service';
import { CacheManagerService } from '@iupics-manager/managers/cache-manager/cache-manager.service';
import { DataStoreService } from '@iupics-manager/managers/data-store/data-store.service';
import { KeybindStateManagerService } from '@iupics-manager/managers/keybind-state-manager/keybind-state-manager.service';
import { MessageManagerService } from '@iupics-manager/managers/message/message-manager.service';
import { RecentItemsManagerService } from '@iupics-manager/managers/recent-items-manager/recent-items-manager.service';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import { UICreatorService } from '@iupics-manager/managers/ui-creator/ui-creator.service';
import { AbstractDynamicComponent } from '@iupics-manager/models/abstract-dynamic-component';
import { DynamicComponent } from '@iupics-manager/models/dynamic-component';
import { Global } from '@iupics-manager/models/global-var';
import { IupicsEvent, IupicsTypeEvent } from '@iupics-manager/models/iupics-event';
import { IupicsMessage } from '@iupics-manager/models/iupics-message';
import { ApizGridUtils } from '@iupics-util/tools/apiz-grid.utils';
import { LogicEvaluator } from '@iupics-util/tools/logic-evaluator';
import { whenRequestsSettle } from '@iupics-util/tools/requests-settle.util';
import {
  ApizGridEvent,
  AppliedItem,
  AutosizeBehavior,
  FilterModel,
  FilterType,
  GridOptionsAppliedItems,
  injectGridApiService,
  provideGridApiService,
  providePanelService,
  RowSelectionMode,
} from '@iupics/apiz-grid';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IupicsMenuType } from '@web-desktop/models/menu-item-ui';
import { cloneDeep } from 'lodash';
import { DomHandler } from 'primeng/dom';
import { ProgressBarModule } from 'primeng/progressbar';
import { ObjectUtils } from 'primeng/utils';
import { Observable, forkJoin, from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import ExportDataUiComponent from '../../../specific/window/export-data-ui/export-data-ui.component';
import ProcessUiComponent from '../../../specific/window/process-ui/process-ui.component';
import CalendarViewUiComponent from '../calendar/calendar-view-ui/calendar-view-ui.component';
import ChartUiComponent from '../chart-ui/chart-ui.component';
import { FilterCtxService } from '../filters/services/filter-ctx.service';
import KanbanUiComponent from '../kanban/kanban-ui/kanban-ui.component';
import { ViewColumnsService } from '../services/view-columns.service';
import TreeUiComponent from '../tree-ui/tree-ui.component';

@Component({
  selector: 'iu-grid-view-ui',
  templateUrl: './grid-view-ui.component.html',
  styleUrls: ['./grid-view-ui.component.scss'],
  animations: [...Global.overlayAnimationAutocomplete, ...Global.overlayAnimationCalendar],
  standalone: true,
  imports: [
    NgClass,
    MenuBarUiComponent,
    ProgressBarModule,
    TreeUiComponent,
    GridTabInfinityScrollHeadlessUiComponent,
    NgStyle,
    KanbanUiComponent,
    CalendarViewUiComponent,
    ChartUiComponent,
    CardsUiComponent,
    forwardRef(() => ModalUiComponent),
    ExportDataUiComponent,
    ProcessUiComponent,
    TranslatePipe,
  ],
  providers: [
    provideGridApiService(), // Provide the grid API service
    providePanelService(), // Provide the panel service
    forwardRef(() => FilterCtxService), // Provide the filterCtx service at this level and not root level
    ViewColumnsService, // Provide the tabColumns service at this level and not root level
  ],
})
export default class GridViewUiComponent
  extends AbstractDynamicComponent
  implements OnInit, AfterViewInit, OnDestroy, ModalDisplayRequester
{
  #dataStoreService = inject(DataStoreService);
  #translateService = inject(TranslateService);
  #riManager = inject(RecentItemsManagerService);
  #zone = inject(NgZone);
  /** recent item waiting for the record's requests (the last opened record only) */
  #cancelRecent: (() => void) | undefined;
  #keybindStateManager = inject(KeybindStateManagerService);
  #messageManager = inject(MessageManagerService);
  #connectorService = inject(SecurityManagerService);
  #uiCreatorService = inject(UICreatorService);
  #config = inject(AppConfig);
  elementRef = inject(ElementRef);
  #viewColumnsService = inject(ViewColumnsService);
  #processService = inject(CompiereProcessService);

  @Input() gridTitle: string;
  @Input() isSpecificGrid = false;
  @Input() isSpecificGridTitleDisplay = true;
  @Input() rowSelection: RowSelectionMode = 'multiple';
  @Input() hasUniversalFilter = true;
  @Input() fromForm = false;
  @Input() hasCheckbox = true;
  @Input() initAutosizeBehavior = AutosizeBehavior.MIN_CONTENT;
  isGridCollapsed = false;

  parentFormID: number;
  @ViewChild(MenuBarUiComponent)
  menuBarUiComponent: MenuBarUiComponent;
  @ViewChild(GridTabInfinityScrollHeadlessUiComponent)
  GridTabInfinityScrollUiComponent: GridTabInfinityScrollHeadlessUiComponent;
  @ViewChild(CalendarViewUiComponent)
  calendarViewUiComponent: CalendarViewUiComponent;
  @ViewChild(KanbanUiComponent)
  kanbanUiComponent: KanbanUiComponent;
  @ViewChild(ChartUiComponent)
  chartUiComponent: ChartUiComponent;
  @ViewChild(TreeUiComponent)
  treeUiComponent: TreeUiComponent;
  @ViewChild(CardsUiComponent)
  cardsUiComponent: CardsUiComponent;
  @ViewChild('panelCalendar')
  panelCalendar: ElementRef;
  @ViewChild('exportDataModal')
  exportDataModal: ModalUiComponent;
  displayProcessUI = false;
  displayFormUI = false;
  processId: number;
  formId: number;
  initFromSwitch = false;
  isTree: boolean;
  isCards: boolean;
  isDisplayed = true;
  whereClause = '';
  visibleButtons = true;
  specificWindowTitle: string;
  /**
   * permet de savoir si il faut lancer une recherche au d�marrage de la fenetre
   */
  isLaunchSearchGrid = true;
  editViewParent: any;
  columnNames;

  @Output() gridCellEditingStopped = new EventEmitter<any>();
  @Output() gridViewCellClicked = new EventEmitter<any>();
  @Output() multiSelectEmitter = new EventEmitter<{ selected: boolean; data: object[] }>();
  @Output() gridRefreshed = new EventEmitter<any>();
  @Output() gridPrefLoaded = new EventEmitter<GridTabInfinityScrollUiComponent>();
  @Output() gridTabAfterViewInitEmitter = new EventEmitter<any>();

  @ViewChild('divContent') divContent: ElementRef;
  @ViewChild('vcrSpecific', { read: ViewContainerRef, static: true }) vcrSpecific: ViewContainerRef;

  @Input() windowType = IupicsMenuType.WINDOW;

  _viewType: ViewType = undefined;
  set viewType(value: any) {
    this._viewType = value;
    // notify
    if (!(this.DOMParentComponent.DOMParentComponent instanceof EditTabUiComponent)) {
      (this.DOMParentComponent as BladeUiComponent).notifyUrlChange();
    }
  }

  get viewType() {
    return this._viewType;
  }

  oldWidth: number;
  private _selectedRecordId;
  set selectedRecordId(value: any) {
    const isDiff = this._selectedRecordId !== value;
    this._selectedRecordId = value;
    // notify
    if (isDiff && !(this.DOMParentComponent instanceof EditTabUiComponent)) {
      (this.DOMParentComponent as BladeUiComponent).notifyUrlChange();
    }
  }

  get selectedRecordId() {
    return this._selectedRecordId;
  }

  @Input() isMenuDisplay = true;
  exportDataOptions: any;
  displayExportDataModal = false;
  displayCustoModal = false;

  isTabTopLevel = true;
  isSplitView = false;
  exportIds: any[];

  noColumnToDisplay = false;

  currentRequest: CompiereDataGridRequestJSON;

  overlayVisible = false;
  label = '';
  isLabelDisplay = false;
  scrollHeight;
  suggestions;
  field;
  ObjectUtils = ObjectUtils;
  itemTemplate;
  noResults;
  emptyMessage;
  highlightOption;

  panelStyleClass;
  panelStyle;
  inline;
  documentClickListener: () => void;
  @Input() suppressRowClickSelection = false;

  modalClass = 'p-col-10 p-md-6 p-lg-4';
  // * searchPanel pour universal filter
  //   displaySearch: boolean;
  searchLinkedComponent: any;
  // *
  isLoading = false;

  infoWindowId?: number;
  searchPanelValidation: string;

  @Input() isZoomTargetGrid = false;

  api = injectGridApiService();
  columnApi = this.api.columnApi;
  filterCtxService = inject(FilterCtxService);
  #filters: AppliedItem<'filter'>[] = [];
  readonly #appliedItemsUpdatedBound = this.#appliedItemsUpdatedHandler.bind(this);

  displayEmailEditor: boolean;
  displayJoinFilesPanel: boolean;

  isMultiProcessOpen = false;

  ngOnInit(): void {
    Global.startPerf(this);
    // this.vcr = this.DOMParentComponent.vcr; // retirer � cause d'un bug avec les zoom target, si probl�me, voir pourquoi cette ligne est n�cessaire. Bon Chance (ref ?)
    this.editViewParent = this.DOMParentComponent;
    if (this.editViewParent !== this.container) {
      while (!(this.editViewParent instanceof EditViewUiComponent)) {
        this.editViewParent = this.editViewParent.DOMParentComponent;
      }
    } else {
      this.editViewParent = undefined;
    }
    this.isTree = this.data.hasTree && this.data.urlTree !== undefined && this.data.urlTree !== null;
    this.isCards = this.data?.tabType !== undefined && typeof this.data?.tabType === 'string';
    if (this.isCards) {
      this.viewType = ViewType.CARDS;
    }

    if (this.data?.isTabTopLevel !== undefined) {
      this.isTabTopLevel = this.data.isTabTopLevel;
    }

    this.subscriptions.push(
      this.#keybindStateManager.splitViewEmitter.subscribe((value: boolean) => (this.isSplitView = value))
    );

    if (this.container?.activeTab?.othersRecordId?.[0]?.tabId == this.tabId) {
      if (this.container.activeTab.othersRecordId[0].recordId === 'newRecord') {
        this.openNew();
      } else {
        const filterModel = this.createFilterModelWithRecordId(this.container.activeTab.othersRecordId[0].recordId);
        this.initGridParams = {
          request: {
            filterModel,
            validation: this.getTabWhereclause(),
            windowCtx: this.getCurrentContext(),
          },
        };
      }
    }
    if (
      this.data.columnsTableHeader.length === 0 &&
      (!this.data.searchColumns || this.data.searchColumns.length === 0)
    ) {
      this.noColumnToDisplay = true;
    }
    this.visibleButtons = this.windowType === IupicsMenuType.WINDOW;

    this.filterCtxService.registerGrid(this);
    this.api.addEventListener('appliedItemsUpdated', this.#appliedItemsUpdatedBound);

    this.#viewColumnsService.updateModalDisplay.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((request) => {
      this.updateModalDisplay(request);
    });
  }

  #appliedItemsUpdatedHandler(e: ApizGridEvent<GridOptionsAppliedItems>): void {
    const previousFilters = [...this.#filters];
    const currentFilters = [...(e.data?.filters ?? [])];
    this.filterCtxService.reinitLocalDatastore();

    for (const filter of previousFilters) {
      const found = currentFilters.find((cf) => cf.colId === filter.colId);
      if (found === undefined) {
        // filtre pr�c�dent non pr�sent dans les courants
        filter.values = filter.filterType === FilterType.SET ? [[]] : [];
        currentFilters.push(filter);
        delete this.GridTabInfinityScrollUiComponent?.query?.filters?.[filter.colId];
      }
    }

    this.filterCtxService.updateLocalDatastore(currentFilters);
    this.#filters = currentFilters.filter((cf) => cf.values.filter((v) => v !== undefined).length);
  }

  ngAfterViewInit(): void {
    // We need to set the viewType to GRID if the tabType is not set, otherwise the grid will not be displayed
    this.viewType ||= ViewType.GRID;
    this.updateAllLogic();
    Global.endPerf(this, '');
  }

  ngOnDestroy(): void {
    this.#cancelRecent?.();
    super.ngOnDestroy();
    this.api?.removeEventListener('appliedItemsUpdated', this.#appliedItemsUpdatedBound);
    this.filterCtxService.unregisterGrid(this);
    this.elementRef = undefined;
    this.vcrSpecific?.clear();
    this.vcrSpecific = undefined;
    this.divContent = undefined;
    this.GridTabInfinityScrollUiComponent = undefined;
  }

  createFilterModelWithRecordId(recordId: string): FilterModel {
    const filterModel: FilterModel = {};
    const splittedRecord = recordId.split(',');
    if (splittedRecord.length > 1) {
      for (let i = 0; i < splittedRecord.length; i += 2) {
        let filterType = null;
        if (splittedRecord[i + 1]) {
          if (!splittedRecord[i].includes('_ID') && this.tabId) {
            filterType = this.#dataStoreService.getColumnCompiereDataGridFilterType(this.tabId, splittedRecord[i]);
          }
          const filter: CompiereDataGridFilterModel = {
            filterType: filterType ?? CompiereDataGridFilterType.NUMBER,
            operators: [OperatorFilterType.EQUALS],
            values: [[splittedRecord[i + 1]]],
          };
          filterModel[splittedRecord[i]] = filter;
        }
      }
      return filterModel;
    }
  }

  openRemainingEditView() {
    /* Continuation du zoomAcross */
    if (this.editViewParent?.zoomInfo?.children) {
      this.zoomAcrossPropagation(this.editViewParent.zoomInfo);
    }
    /*divers edit � ouvrir*/
    if (this.container?.activeTab?.othersRecordId?.length > 0) {
      this.openOtherEditViews();
    }
  }

  forceOpenEditView(record_ID, zoomInfo?) {
    const dataStoreRequest: DataStoreRequest = {
      windowId: this.data.AD_Window_ID,
      record_id: record_ID,
      parent_constraint: this.parentTab ? this.parentTab.dataStoreKey.recordId : undefined,
      compiereRequest: {
        windowType: CompiereDataGridType.WINDOW,
        entityId: this.tabId,
        startRow: 0,
        endRow: 1,
        validation: this.getTabWhereclause(),
        windowCtx: this.getCurrentContext(),
      },
    };
    if (
      dataStoreRequest.parent_constraint !== null &&
      dataStoreRequest.parent_constraint !== undefined &&
      dataStoreRequest.parent_constraint.length > 0
    ) {
      let filtersArray = dataStoreRequest.parent_constraint.split('=');
      if (filtersArray.length <= 1) {
        const splittedRecord = dataStoreRequest.parent_constraint.split(',');
        filtersArray = splittedRecord.length > 1 ? splittedRecord : [];
      }
      if (filtersArray.length == 2) {
        if (
          !this.#dataStoreService.getColumnCompiereDataGridFilterType(
            dataStoreRequest.compiereRequest.entityId,
            filtersArray[0]
          )
        ) {
          dataStoreRequest.parent_constraint = '';
        }
      }
    }

    this.subscriptions.push(
      this.#dataStoreService.getWindowSingleData(dataStoreRequest).subscribe((data) => {
        if (data) {
          const parentComp = this.getFirstEditViewParent(this.DOMParentComponent);
          if (parentComp) {
            parentComp.gridTabIdSelected = this.tabId;
          }
          this.openEditView(zoomInfo);
          if (this.isTabTopLevel) {
            const ri = new RecentItem(
              `${this.data.label}: ${this.#riManager.getInfoLabel(data.data)}`,
              data.key.tabId,
              data.key.windowId,
              data.data.Data_UUID
            );
            this.#afterRecordShown(() => this.#riManager.pushRecentItem(ri).subscribe());
          }
          this.updateData(data.key);
        }
      })
    );
  }

  onRowSelectedOnGridTab(dataStoreKey: DataStoreKey) {
    this.gridViewCellClicked.emit(dataStoreKey); // smart button
    if (this.checkEditViewCreated()) {
      const parentComp = this.getFirstEditViewParent(this.DOMParentComponent);
      if (parentComp) {
        parentComp.gridTabIdSelected = this.tabId;
      }
    }
    this.openEditView();
    if (this.isTabTopLevel) {
      const request: DataStoreRequest = {
        windowId: (this.container as BladeUiComponent).infoComponent.windowId,
        record_id: dataStoreKey.recordId,
        parent_constraint: '',
        compiereRequest: {
          windowType: CompiereDataGridType.WINDOW,
          entityId: this.tabId,
          startRow: 0,
          endRow: 1,
          validation: this.getTabWhereclause(),
          windowCtx: this.getCurrentContext(),
        },
      };
      this.#afterRecordShown(() =>
        this.subscriptions.push(this.#riManager.addRecentItem(request, this.data.label).subscribe())
      );
    }
    this.updateData(dataStoreKey);
  }

  /** recent items are not urgent: sent once the record is read, so they don't queue before it */
  #afterRecordShown(fn: () => void): void {
    this.#cancelRecent?.();
    this.#cancelRecent = whenRequestsSettle(this.#zone, () => {
      this.#cancelRecent = undefined;
      fn();
    });
  }

  /** a delete question is open (a second click does not ask twice) */
  #askingDelete = false;

  remove(event: MouseEvent): void {
    if (this.#askingDelete) return;
    const dataUUIDs = this.#dataStoreService.getDataUUIDFromTabID(this.data.AD_Tab_ID);
    const recordIds = [];
    const selectedNodes = this.GridTabInfinityScrollUiComponent.grid.api.getSelectedNodes();
    for (const node of selectedNodes) {
      const recordId = {};
      for (const dataUUID of dataUUIDs) {
        const data = node.getRowData()[dataUUID];
        if (data instanceof Object) {
          recordId[dataUUID] = data.id;
        } else {
          recordId[dataUUID] = data;
        }
      }
      recordIds.push(recordId);
    }
    const dataStoreKey: DataStoreKey = {
      windowId: this.data.AD_Window_ID,
      tabId: this.data.AD_Tab_ID,
      recordId: '',
      parentId: this.editViewParent
        ? this.editViewParent.currentDataStoreKey
          ? this.editViewParent.currentDataStoreKey.recordId
          : ''
        : '',
    };
    if (recordIds.length === 0) return;
    // ask before deleting: the action cannot be undone
    this.#askingDelete = true;
    Global.infoDialog.message = {
      summary: this.#translateService.instant('infodialog.dialogs.delete.title'),
      detail: this.#translateService.instant('gridView.deleteConfirm', { count: recordIds.length }),
    };
    Global.infoDialog.dialogType = InfoDialogType.CONFIRM_YESNO;
    Global.infoDialog.confirmLabel = this.#translateService.instant('generic.delete');
    Global.infoDialog.cancelLabel = this.#translateService.instant('generic.cancel');
    Global.infoDialog.danger = true;
    Global.infoDialog.showInfoDialog();
    const done = () => {
      this.#askingDelete = false;
      confirm.unsubscribe();
      cancel.unsubscribe();
    };
    const confirm = Global.infoDialog.confirm.subscribe(() => {
      done();
      this.#deleteRecords(dataStoreKey, recordIds);
    });
    const cancel = Global.infoDialog.cancel.subscribe(() => done());
  }

  #deleteRecords(dataStoreKey: DataStoreKey, recordIds: any[]): void {
    this.isLoading = true;
    this.#dataStoreService
      .deleteWindowData(dataStoreKey, recordIds)
      .pipe(
        switchMap((res) =>
          res.success > 0 ? this.#riManager.deleteRecentItems(dataStoreKey, recordIds).pipe(map(() => res)) : of(res)
        )
      )
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          if (res) {
            if (this.GridTabInfinityScrollUiComponent) {
              this.GridTabInfinityScrollUiComponent.grid.api.deselectAll();
              this.refreshGridAndParent();
            }
            this.#messageManager.newMessage(
              new IupicsMessage(
                this.#translateService.instant('gridView.deleteMessageTitle'),
                this.#translateService.instant('gridView.deletedLines') +
                  ' : ' +
                  JSON.stringify(res.success) +
                  (res.errors.length === 0
                    ? ''
                    : ' / ' + this.#translateService.instant('gridView.errors') + ' : ' + res.errors.length),
                res.errors.length === 0 ? 'success' : res.success > 0 ? 'warning' : 'error'
              )
            );
          } else {
            throw new Error(this.#translateService.instant('gridView.deleteErrorMessage'));
          }
        },
        error: (error) => {
          this.isLoading = false;
          throw new Error(error?.error?.message ?? error.message);
        },
      });
  }

  openNew() {
    if (!this.checkAndExecuteOverride('new')) {
      if (this.editViewParent === null || this.editViewParent === undefined) {
        this.DOMParentComponent.notifyUrlChange('newRecord');
      }
      if (this.GridTabInfinityScrollUiComponent) {
        if (!this.GridTabInfinityScrollUiComponent.isTabTopLevel) {
          const parentComp = this.getFirstEditViewParent(this.DOMParentComponent);
          if (parentComp) {
            if (!this.#dataStoreService.checkDataBeforeNewLine(parentComp.currentDataStoreKey)) {
              parentComp.beforeSave(null).then((_) => {
                parentComp.gridTabIdSelected = this.tabId;
                this.openEditView();
                this.updateData(
                  this.#dataStoreService.newWindowData(
                    this.container.infoComponent.windowId,
                    this.tabId,
                    null,
                    EditViewUtils.getParentDatastoreKeyFromGrid(this)
                  ).key
                );
              });
            } else {
              this.openEditView();
              this.updateData(
                this.#dataStoreService.newWindowData(
                  this.container.infoComponent.windowId,
                  this.tabId,
                  null,
                  EditViewUtils.getParentDatastoreKeyFromGrid(this)
                ).key
              );
            }
            // }
          }
        } else {
          if (this.checkEditViewCreated()) {
            const parentComp = this.getFirstEditViewParent(this.DOMParentComponent);
            if (parentComp) {
              parentComp.gridTabIdSelected = this.tabId;
            }
          }
          this.openEditView();
          this.updateData(
            this.#dataStoreService.newWindowData(
              this.container.infoComponent.windowId,
              this.tabId,
              null,
              EditViewUtils.getParentDatastoreKeyFromGrid(this)
            ).key
          );
        }
      }
    }
  }

  /*
   * processModal: { display: { key: 'displayProcessUI', value: boolean }, id: { key: 'processId', value: number } }
   * formModal: { display: { key: 'displayFormUI', value: boolean }, id: { key: 'formId', value: number } }
   */
  updateModalDisplay({ display, id }: ModalDisplayRequest): void {
    this.displayModal(display, id);
  }

  displayModal(display: ModalDisplayRequest['display'], id?: ModalDisplayRequest['id']): void {
    this[display.key] = display.value;
    if (id && display.value === true) {
      this[id.key] = id.value;
      if (id.key !== 'processId') {
        this.createSpecificWindow(id.value);
      }
      if (display.sourceComponent?.from && display.sourceComponent?.from === 'multiProcess') {
        this.isMultiProcessOpen = true;
      }
    } else {
      this.isMultiProcessOpen = false;
    }
  }

  createSpecificWindow(formId: number) {
    this.subscriptions.push(
      this.#uiCreatorService.getSpecificWindow(formId).subscribe((specificWindow) => {
        let component;
        if (
          specificWindow.angularClass &&
          specificWindow.angularClass.length > 0 &&
          specificWindow.angularClass !== 'default'
        ) {
          component = CacheManagerService.iupics_specific_window.get(specificWindow.angularClass);
        }
        if (!component) {
          component = CacheManagerService.iupics_specific_window.get('default');
        }
        this.vcrSpecific.clear();
        const componentRef = this.vcrSpecific.createComponent<SpecificWindowUiComponent>(component);
        this.specificWindowTitle = specificWindow.name;
        componentRef.instance.name = specificWindow.name;
        componentRef.instance.title = specificWindow.title;
        componentRef.instance.description = specificWindow.description;
        componentRef.instance.help = specificWindow.help;
        // componentRef.instance.iconClass = specificWindow.iconClass;
        componentRef.instance.componentRef = componentRef;
        // componentRef.instance.id = specificWindow.id;
        componentRef.instance.isModal = true;
        componentRef.instance.formId = formId;
        componentRef.instance.vcrwindow = this.vcrSpecific;
        componentRef.instance.parentComponent = this;
        componentRef.instance.index = this.vcrSpecific.length - 1;
        componentRef.instance.closeModalEmitter.subscribe({
          next: () => this.updateModalDisplay(ModalDisplayRequest.Form(false)),
        });
        this.componentRefs.push(componentRef);
      })
    );
  }

  getFirstEditViewParent(component): EditViewUiComponent {
    // find out if the row is from the main gridview or not
    let parentComp = component;
    while (parentComp && !(parentComp instanceof EditViewUiComponent)) {
      parentComp = parentComp.DOMParentComponent;
    }
    return parentComp as EditViewUiComponent;
  }

  deleteLinkedEditView() {
    const editViewParent = this.getFirstEditViewParent(this.DOMParentComponent);
    if (editViewParent) {
      editViewParent.gridTabIdSelected = null;
    }
  }

  checkEditViewCreated(): boolean {
    const parentComp = this.getFirstEditViewParent(this.DOMParentComponent);
    // check if edit view has already been opened earlier
    return parentComp
      ? parentComp.gridTabIdSelected !== null && parentComp.gridTabIdSelected !== undefined
      : this.isEditExist();
  }

  openEditView(zoomInfo?) {
    if (!this.isEditExist()) {
      const item: DynamicComponent = {
        container: this.container,
        DOMParentComponent: this.container,
        linkedComponents: [this],
        component: 'EditViewUiComponent',
        cssClass: `iupics-blade-content ${this.data?.AD_Form_ID ? 'iupics-specific-blade-content' : ''}`,
        isCssOnComponent: false,
        tabId: this.tabId,
        windowId: this.data.AD_Window_ID,
        zoomInfo: zoomInfo,
        parentStore: this.editViewParent ? this.editViewParent.editTabs[0].dataStored : null,
      };
      let i = 1;
      let find = false;
      while (i < this.container.DOMChildrenComponent.length && !find) {
        if (
          this.container.DOMChildrenComponent[i] === this.editViewParent &&
          ++i < this.container.DOMChildrenComponent.length
        ) {
          (this.container.DOMChildrenComponent[i] as EditViewUiComponent).removeComponent();
          find = true;
        } else {
          i++;
        }
      }
      this.componentEmitter.emit({
        type: IupicsTypeEvent.showEditView,
        item: item,
      });
    } else {
      // check si on doit agrandir l'editview existante
      if (this.container?.breadcrumbComponent) {
        const breadCrumb = (this.container as BladeUiComponent).breadcrumbComponent;
        if (breadCrumb) {
          const currentTabId = this.editViewParent ? this.editViewParent.tabId : 0;
          const index = breadCrumb.model.findIndex((item) => parseInt(item.id, 10) === currentTabId && !item.disabled);
          if (index > -1 && breadCrumb.model.length > index + 1) {
            breadCrumb.itemClick(null, breadCrumb.model[index + 1]);
          }
        }
      }
    }
  }

  updateData(dataStoreKey: DataStoreKey, fromNew = false) {
    if (fromNew) {
      this.notifierLinkedComponent.next({
        type: IupicsTypeEvent.selectDataChange,
        item: {
          container: null,
          dataStoreKey: dataStoreKey,
        },
      });
    } else {
      // si une edit view n'est pas encore cr�er on n'appelle pas de popup
      this.notifierLinkedComponent.next({
        type: this.isEditExist() ? IupicsTypeEvent.checkBeforeChange : IupicsTypeEvent.selectDataChange,
        item: {
          container: null,
          dataStoreKey: dataStoreKey,
        },
      });
    }
  }

  checkBeforeChange(editView: EditViewUiComponent, dataStoreKey: DataStoreKey) {
    if (editView.checkData()) {
      if (!editView.transmitDataChange(dataStoreKey, this.tabId)) {
        this.onRowSelectedOnGridTab(dataStoreKey);
      }

      return;
    }

    const ctx = this.#connectorService.getIupicsUserContext();
    if (ctx['AutoCommit'] === 'Y') {
      editView.beforeSave(null);
      return;
    }

    Global.infoDialog.message = {
      summary: this.#translateService.instant('infodialog.dialogs.checkBefore.close.title'),
      detail: this.#translateService.instant('infodialog.dialogs.checkBefore.close.message'),
    };
    Global.infoDialog.dialogType = InfoDialogType.CONFIRM_YESNO;
    Global.infoDialog.showInfoDialog();

    const confirm = Global.infoDialog.confirm.subscribe({
      next: () => {
        editView.beforeSave(null);
        if (
          editView.conflictsResult &&
          editView.conflictsResult.hasConflicts === false &&
          editView.conflictsResult.mustRefresh === false
        ) {
          if (!editView.transmitDataChange(dataStoreKey, this.tabId)) {
            this.onRowSelectedOnGridTab(dataStoreKey);
          }
        }
        confirm.unsubscribe();
        cancel.unsubscribe();
      },
    });

    const cancel = Global.infoDialog.cancel.subscribe({
      next: () => {
        if (editView.editTabs[0].dataStored.status !== DataStoreStatus.NEWRECORD) {
          this.subscriptions.push(
            this.#dataStoreService.syncWithRemoteWindowData(editView.currentDataStoreKey).subscribe()
          );
        } else {
          this.#dataStoreService.deleteDataFromStoreOnly(editView.currentDataStoreKey);
        }
        if (!editView.transmitDataChange(dataStoreKey, this.tabId)) {
          this.onRowSelectedOnGridTab(dataStoreKey);
        }
        confirm.unsubscribe();
        cancel.unsubscribe();
      },
    });
  }

  onChildUpdate(event): void {
    // resize grid when its opened
    if (event.type === IupicsTypeEvent.showGridView && this.GridTabInfinityScrollUiComponent && !this.isGridCollapsed) {
      this.GridTabInfinityScrollUiComponent.refresh();
    }
  }

  onSiblingUpdate(event: IupicsEvent) {
    switch (event.type) {
      case IupicsTypeEvent.collapseEvent:
        this.rowSelection = 'single';
        this.menuBarUiComponent.setVisibleButton(false);
        this.isGridCollapsed = true;
        if (!Global.isMobile()) {
          this.oldWidth = this.oldWidth ?? this.divContent.nativeElement.clientWidth;
          if (this.GridTabInfinityScrollUiComponent) {
            this.isTree = false;
            this.GridTabInfinityScrollUiComponent.onlyOneColumn();
          }
          if (this.calendarViewUiComponent) {
            setTimeout(() => {
              this.calendarViewUiComponent.calendar.updateSize();
            }, 0);
          }
        } else {
          // grid fill width of device
          this.oldWidth = Global.getDeviceWidth();
        }
        if (this.editViewParent?.editTabs?.length > 1) {
          for (const tab of this.editViewParent.editTabs) {
            tab.collapseTab();
          }
          // this.editViewParent.smartButtons.resizeSmartButton(event);
        }
        break;
      case IupicsTypeEvent.expandEvent:
        this.rowSelection = 'multiple';
        this.menuBarUiComponent.setVisibleButton(true);
        this.isGridCollapsed = false;

        if (!Global.isMobile()) {
          if (this.GridTabInfinityScrollUiComponent) {
            this.isTree = this.data.hasTree;
            let parentComp = this.DOMParentComponent;
            while (parentComp && !(parentComp instanceof EditViewUiComponent)) {
              parentComp = parentComp.DOMParentComponent;
            }

            this.GridTabInfinityScrollUiComponent.allColumns(event?.item?.data?.label === 'select');
          }
          if (this.calendarViewUiComponent) {
            setTimeout(() => {
              this.calendarViewUiComponent.calendar.updateSize();
            }, 0);
          }
        }

        if (this.editViewParent?.editTabs?.length > 1) {
          for (const tab of this.editViewParent.editTabs) {
            tab.expandTab();
          }
          // this.editViewParent.smartButtons.resizeSmartButton(event);
        }
        break;
      case IupicsTypeEvent.REFRESH_GRID:
        this.refreshGridAndParent();
        break;
      case IupicsTypeEvent.triggerAfterChange:
        this.notifierLinkedComponent.next({
          type: IupicsTypeEvent.selectDataChange,
          item: event.item,
        });
        break;
      case IupicsTypeEvent.UPDATE_ROWSAVED:
        this.editViewParent?.updateAll(false, this.tabId);
        this.updateRow(event.item ? event.item.dataStoreKey.recordId : this.selectedRecordId);
        break;
      default:
        break;
    }
  }

  refreshGridAndParent() {
    if (this.GridTabInfinityScrollUiComponent) {
      // setter la page courante
      this.GridTabInfinityScrollUiComponent.currentSelectPageIndex =
        this.GridTabInfinityScrollUiComponent.grid.api.getCurrentPage();
      if (this.editViewParent) {
        this.editViewParent.updateAll(false);
      } else {
        this.GridTabInfinityScrollUiComponent.refresh(true);
      }
    }
  }

  onRemoveComponent(event: IupicsEvent) {}

  updateRow(recordId: string) {
    this.GridTabInfinityScrollUiComponent.grid.api.forEachNode((node) => {
      const data = node.getRowData();
      if (data && data['Data_UUID'] == recordId) {
        const request: DataStoreRequest = {
          windowId: this.data.AD_Window_ID,
          record_id: recordId,
          parent_constraint: this.editViewParent?.currentDataStoreKey?.recordId ?? '',
          compiereRequest: {
            windowType: CompiereDataGridType.WINDOW,
            entityId: this.tabId,
            startRow: 0,
            endRow: 1,
            validation: this.getTabWhereclause(),
            windowCtx: this.getCurrentContext(),
          },
        };

        if (
          request.parent_constraint !== null &&
          request.parent_constraint !== undefined &&
          request.parent_constraint.length > 0
        ) {
          let filtersArray = request.parent_constraint.split('=');
          if (filtersArray.length <= 1) {
            const splittedRecord = request.parent_constraint.split(',');
            filtersArray = splittedRecord.length > 1 ? splittedRecord : [];
          }

          if (
            filtersArray.length == 2 &&
            !this.#dataStoreService.getColumnCompiereDataGridFilterType(
              request.compiereRequest.entityId,
              filtersArray[0]
            )
          ) {
            request.parent_constraint = '';
          }
        }

        this.#dataStoreService.getWindowSingleData(request).subscribe((dataStore) => {
          if (dataStore) {
            this.GridTabInfinityScrollUiComponent.grid.api.getRowNode(node.id).setData({ ...dataStore.data });

            for (let i = 1; i < this.container.DOMChildrenComponent.length; i++) {
              if (
                this.container.DOMChildrenComponent[i] instanceof EditViewUiComponent &&
                this.container.DOMChildrenComponent[i].tabId === this.tabId &&
                this.container.DOMChildrenComponent[i]?.currentDataStoreKey?.parentId
              ) {
                const parentColumnName =
                  this.container.DOMChildrenComponent[i].currentDataStoreKey.parentId.split(',')[0];
                const parentId = this.container.DOMChildrenComponent[i].currentDataStoreKey.parentId.split(',')[1];

                if (
                  dataStore.data[parentColumnName] &&
                  dataStore.data[parentColumnName].id !== parseInt(parentId, 10)
                ) {
                  this.GridTabInfinityScrollUiComponent.grid.api.deselectAll();
                  this.refreshGridAndParent();
                }
              }
            }
          } else {
            console.error('No data found!');
          }
        });
      }
    });
  }

  displayHideFilters(isFilter) {
    this.GridTabInfinityScrollUiComponent?.displayHideFilters(isFilter);
  }

  //#region toolbar processes against the selected rows
  readonly #fieldStateService = inject(FieldStateService);
  /** above this, the rows are not checked (one record read per row) */
  static readonly PROCESS_CHECK_MAX_ROWS = 200;

  /**
   * For each toolbar process: how many selected rows hide it or do not allow it, with the rules of the edit view
   * button (tab read-only, waiting validation, inactive, processed, read-only / not updatable field, access level,
   * read-only and display logic, @SQL included). Null when there are too many rows to check.
   */
  checkProcessesForSelection(
    processes: ToolbarProcess[]
  ): Observable<Record<string, ToolbarProcessSelectionState> | null> {
    const rows = this.GridTabInfinityScrollUiComponent?.grid?.api.getSelectedRows() ?? [];
    const checked = processes.filter((p) => p.field);
    if (!rows.length || !checked.length) return of({});
    if (rows.length > GridViewUiComponent.PROCESS_CHECK_MAX_ROWS) return of(null);
    return from(rows).pipe(
      // the full record (the grid row has only the listed columns), 6 reads at a time
      mergeMap((row) => this.#loadRowStore(row['Data_UUID']), 6),
      mergeMap((dataStore) =>
        forkJoin(
          checked.map((p) =>
            forkJoin([this.#processDisplayed(p.field, dataStore), this.#processReadOnly(p.field, dataStore)]).pipe(
              map(([displayed, readOnly]) => ({ uuid: p.componentUUID, displayed, readOnly }))
            )
          )
        )
      ),
      toArray(),
      map((perRow) => {
        const states: Record<string, ToolbarProcessSelectionState> = {};
        for (const p of checked) states[p.componentUUID] = { total: perRow.length, blocked: 0, hidden: 0 };
        for (const results of perRow) {
          for (const r of results) {
            if (!r.displayed) states[r.uuid].hidden++;
            if (!r.displayed || r.readOnly) states[r.uuid].blocked++;
          }
        }
        return states;
      })
    );
  }

  #loadRowStore(recordId: string): Observable<DataStore> {
    const request: DataStoreRequest = {
      windowId: this.data.AD_Window_ID,
      record_id: recordId,
      parent_constraint: this.editViewParent?.currentDataStoreKey?.recordId ?? '',
      compiereRequest: {
        windowType: CompiereDataGridType.WINDOW,
        entityId: this.tabId,
        startRow: 0,
        endRow: 1,
        validation: this.getTabWhereclause(),
        windowCtx: this.getCurrentContext(),
      },
    };
    if (request.parent_constraint?.length > 0) {
      let filtersArray = request.parent_constraint.split('=');
      if (filtersArray.length <= 1) {
        const splittedRecord = request.parent_constraint.split(',');
        filtersArray = splittedRecord.length > 1 ? splittedRecord : [];
      }
      if (
        filtersArray.length == 2 &&
        !this.#dataStoreService.getColumnCompiereDataGridFilterType(request.compiereRequest.entityId, filtersArray[0])
      ) {
        request.parent_constraint = '';
      }
    }
    return this.#dataStoreService.getWindowSingleData(request);
  }

  #processDisplayed(field: ToolbarProcessField, dataStore: DataStore): Observable<boolean> {
    if (!dataStore?.data) return of(false);
    if (!field.displayLogic) return of(true);
    const ctx = this.getCurrentContext(dataStore);
    if (this.#isSqlLogic(field.displayLogic)) {
      return this.#fieldStateService
        .getEntityState<any>({
          fieldType: CompiereDataFieldType.FIELD,
          id: field.fieldId ?? field.columnId,
          states: [ItemStateType.DISPLAYED],
          windowCtx: ctx,
        })
        .pipe(
          map((state) => state?.displayed !== false),
          catchError(() => of(true))
        );
    }
    return of(LogicEvaluator.evaluateLogic(ctx, field.displayLogic));
  }

  /** same order as AbstractDataContainer.checkStandardWindowReadonly */
  #processReadOnly(field: ToolbarProcessField, dataStore: DataStore): Observable<boolean> {
    const data = dataStore?.data;
    if (!data) return of(true);
    if (field.isAlwaysUpdatable === true) return of(false);
    if (this.isReadOnly) return of(true);
    const statusData = this.isTabTopLevel ? data : (this.getParentStore()?.data ?? data);
    const docStatus = statusData?.['DocStatus'];
    if ((docStatus?.id ?? docStatus) === 'WV') return of(true);
    if (['IsActive', 'Posted', 'GenerateTo', 'Record_ID'].includes(field.columnName)) return of(false);
    if (data['IsActive'] === 'N') return of(true);
    const ctx = this.getCurrentContext(dataStore);
    if (ctx?.['Processed'] === 'Y') return of(true);
    if (field.isReadOnly === true) return of(true);
    if (field.isUpdateable === false) return of(true);
    if (!this.#canUpdate(data)) return of(true);
    if (!field.readOnlyLogic) return of(false);
    if (this.#isSqlLogic(field.readOnlyLogic)) {
      return this.#fieldStateService
        .getEntityState<any>({
          fieldType: CompiereDataFieldType.FIELD,
          id: field.fieldId ?? field.columnId,
          states: [ItemStateType.READONLY],
          windowCtx: ctx,
        })
        .pipe(
          map((state) => state?.readonly === true),
          catchError(() => of(false))
        );
    }
    return of(LogicEvaluator.evaluateLogic(ctx, field.readOnlyLogic));
  }

  #isSqlLogic(logic: string): boolean {
    return !!logic && logic.trim().toLowerCase().startsWith('@sql=');
  }

  /** same rule as AbstractDataContainer.canUpdate (user level against the record client / org) */
  #canUpdate(data: any): boolean {
    const toId = (value: any) => {
      const raw = value?.id ?? value;
      const id = parseInt(raw, 10);
      return isNaN(id) ? -1 : id;
    };
    if (data?.['AD_Client_ID'] === undefined || data?.['AD_Client_ID'] === null) return true;
    if (data?.['AD_Org_ID'] === undefined || data?.['AD_Org_ID'] === null) return true;
    const clientID = toId(data['AD_Client_ID']);
    const orgID = toId(data['AD_Org_ID']);
    if (clientID === -1 || orgID === -1) return true;
    const userLevel = (this.#connectorService.getIupicsUserContext()['#User_Level'] as string) ?? '';
    if (clientID === 0 && orgID === 0) return userLevel.indexOf('S') !== -1;
    if (clientID !== 0 && orgID === 0) return userLevel.indexOf('C') !== -1;
    return userLevel.indexOf('O') !== -1;
  }
  //#endregion

  /** rows selected in the grid (selection bar from 2 rows: one row is the usual click selection) */
  selectedCount = signal(0);

  clearSelection(): void {
    this.GridTabInfinityScrollUiComponent?.grid?.api.deselectAll();
    this.displayHideMultiButton({ selected: false, data: [] });
  }

  displayHideMultiButton(event: { selected: boolean; data: object[] }) {
    this.selectedCount.set(event?.selected ? (event.data?.length ?? 0) : 0);
    this.menuBarUiComponent?.setVisibleButtonMultiSelection(event.selected);
    this.multiSelectEmitter.emit(event);
  }

  refreshGrid(query?: SpecificGridQuery) {
    switch (this.viewType) {
      case ViewType.GRID:
        this.GridTabInfinityScrollUiComponent?.refresh(true, query);
        break;
      case ViewType.KANBAN:
        this.kanbanUiComponent?.refresh();
        break;
      case ViewType.CALENDAR:
        this.calendarViewUiComponent?.refresh();
        break;
      case ViewType.CHART:
        this.chartUiComponent?.refresh();
        break;
      case ViewType.CARDS:
        this.cardsUiComponent?.refreshCards();
        break;
      default:
        break;
    }
  }

  openExportDataModal(event: Event) {
    event.stopPropagation();
    if (this.checkAndExecuteOverride('export')) {
      return;
    }

    // R�cup�ration des records s�lectionn�s
    this.exportIds = [];
    if (this.editViewParent?.currentDataStoreKey) {
      const splittedRecord = this.editViewParent.currentDataStoreKey.recordId.split(',');
      if (splittedRecord.length > 1) {
        const tableName = this.data.tableName;
        this.whereClause = '';
        if (tableName) {
          this.whereClause += tableName;
        }
        this.whereClause += '.' + splittedRecord[0] + '=' + splittedRecord[1];
      }
    }
    this.exportIds = this.GridTabInfinityScrollUiComponent.getExportSelections();
    this.displayExportDataModal = true;
    this.initCurrentRequest();
  }
  initCurrentRequest() {
    if (this.viewType === ViewType.GRID) {
      this.currentRequest = this.GridTabInfinityScrollUiComponent.currentRequest;
    } else {
      this.currentRequest = ApizGridUtils.cleanDataRequestForCompiereRequest(this.api.getRequest());
      this.currentRequest.windowType = CompiereDataGridType.WINDOW;
      this.currentRequest.entityId = this.tabId;
    }
  }
  /**
   * Find an edit which has the current tabId in the container
   * @param {any}container
   * @param {number}tabId
   * @returns {number} index
   */
  isEditExist(): boolean {
    let find = false;
    let j = 1;
    while (!find && this.container.DOMChildrenComponent.length > j) {
      if (
        this.container.DOMChildrenComponent[j].DOMComponent.instance instanceof EditViewUiComponent &&
        this.container.DOMChildrenComponent[j].tabId === this.tabId
      ) {
        find = true;
      }
      j++;
    }
    return find;
  }

  openOtherEditViews() {
    if (this.container.activeTab.othersRecordId[0].tabId == this.tabId) {
      if (this.container.activeTab.othersRecordId[0].recordId === 'newRecord') {
        this.openNew();
      } else {
        this.forceOpenEditView(this.container.activeTab.othersRecordId[0].recordId);
        this.selectedRecordId = this.container.activeTab.othersRecordId[0].recordId;
      }
      this.container.activeTab.othersRecordId.splice(0, 1);
      if (this.container.activeTab.othersRecordId.length === 0) {
        this.container.activeTab.othersRecordId = undefined;
      }
    }
  }

  zoomAcrossPropagation(zoomInfo) {
    const nbChildren = zoomInfo.children.length;
    if (nbChildren > 0 && this.tabId == zoomInfo.children[nbChildren - 1].Tab_ID) {
      const concernedChild = zoomInfo.children[nbChildren - 1];
      zoomInfo.children.splice(-1, 1);
      const zoomInfoParam = {
        parentId: zoomInfo.dataUUID,
        windowId: concernedChild.Window_ID,
        dataUUID: concernedChild.Record_ID,
        record_id: concernedChild.Record_ID.split(',')[1],
        children: zoomInfo.children,
      };
      this.forceOpenEditView(concernedChild.Record_ID, zoomInfoParam);
    }
  }

  notifyGridTabAfterViewInitEmitter(gridTab: GridTabInfinityScrollUiComponent) {
    this.gridTabAfterViewInitEmitter.emit(gridTab);
  }

  switchViewType(viewType: ViewType) {
    this.initGridParams = { request: { ...this.currentRequest } };
    this.viewType = viewType;
    this.initFromSwitch = true;
    if (viewType === ViewType.GRID) {
      this.GridTabInfinityScrollUiComponent.setView();
      this.GridTabInfinityScrollUiComponent?.createServerDataSource();
    }

    this.refreshGrid();
  }

  gridCellClicked(event) {
    this.gridViewCellClicked.emit(event);
  }

  //#endregion

  isOutsideClicked(event) {
    return !(
      this.panelCalendar.nativeElement.isSameNode(event.target) ||
      this.panelCalendar.nativeElement.contains(event.target) ||
      this.isNavIconClicked(event)
    );
  }

  isNavIconClicked(event: Event) {
    return (
      DomHandler.hasClass(event.target, 'p-datepicker-prev') ||
      DomHandler.hasClass(event.target, 'p-datepicker-prev-icon') ||
      DomHandler.hasClass(event.target, 'p-datepicker-next') ||
      DomHandler.hasClass(event.target, 'p-datepicker-next-icon') ||
      DomHandler.hasClass(event.target, 'p-datepicker-trigger') ||
      DomHandler.hasClass(event.target, 'ui-input-calendar')
    );
  }

  /**
   * mise � jour du display
   * @param dataStore noveau datastore � prendre en compte
   */
  updateDisplayLogic(dataStore?: DataStore) {
    if (this.data?.displayLogic) {
      this.isDisplayed = LogicEvaluator.evaluateLogic(
        this.getCurrentContext(dataStore) ?? null,
        this.data.displayLogic
      );
    } else {
      this.isDisplayed = true;
    }

    return this.isDisplayed;
  }

  getParentStore() {
    return this.editViewParent?.editTabs?.[0]?.dataStored ?? this.container?.dataStore ?? null;
  }

  /**
   * r�cup�ration du contexte complet du composant
   * @param dataStore nouveau datastore � prendre en compte
   */
  getCurrentContext(datastore?: DataStore) {
    let currentParent;
    let dataStored;
    /* dans le cas d'une grid dans une editview */
    if (this.editViewParent?.editTabs?.[0]) {
      dataStored = this.editViewParent.editTabs[0].dataStored;
      if (this.editViewParent) {
        currentParent = this.editViewParent;
      }
    } else if (this.container) {
      /* dans le cas d'une grid dans une form */
      dataStored = this.container.dataStore;
      if (this.container.parentComponent?.linkedComponents?.[0]) {
        currentParent = this.container.parentComponent;
      }
      /*cas d'une form dans une form */
      if (this.container?.parentComponent?.dataStore) {
        /*cas d'une form dans une form provenant d'un autocomplete */
        return EditViewUtils.mergeCurrentDataDeepCopy(
          dataStored.data,
          (this.container?.sourceComponent ?? this.container.parentComponent).getCurrentContext(),
          false
        );
      }
    }
    /*ajout du tabId */
    dataStored = datastore ?? dataStored;
    if (dataStored?.data) {
      dataStored = {
        ...dataStored,
        data: {
          ...dataStored.data,
          Parent_Tab_ID: this.tabId ?? this.parentTab?.tabId ?? null,
        },
      };
    }

    return EditViewUtils.getCurrentContext(currentParent, dataStored, this.#connectorService.getIupicsUserContext());
  }

  /**
   * permet d'overrider l'action si il existe un process ou une form. La m�thode retourne true si c'est le cas.
   * @param source indique quelle action est � overrider
   */
  checkAndExecuteOverride(source: string) {
    const ad_window_id = this.data ? '' + this.data.AD_Window_ID : '';

    const { formIdToOpen, processIdToOpen } = this.#setFormAndProcessIdToOpen(source, ad_window_id);

    if (formIdToOpen !== undefined) {
      if (this.editViewParent?.updateModalDisplay) {
        (this.editViewParent as ModalDisplayRequester).updateModalDisplay(
          ModalDisplayRequest.Form(true, formIdToOpen, { columnName: source })
        );
      } else {
        this.updateModalDisplay(ModalDisplayRequest.Form(true, formIdToOpen));
      }
    } else if (processIdToOpen !== undefined) {
      if (this.editViewParent?.updateModalDisplay) {
        (this.editViewParent as ModalDisplayRequester).updateModalDisplay(
          ModalDisplayRequest.Process(true, processIdToOpen, source)
        );
      } else {
        this.updateModalDisplay(ModalDisplayRequest.Process(true, processIdToOpen));
      }
    }

    return formIdToOpen !== undefined || processIdToOpen !== undefined;
  }

  #setFormAndProcessIdToOpen(
    source: string,
    ad_window_id: string
  ): { formIdToOpen?: number; processIdToOpen?: number } {
    let key = undefined;
    let formIdToOpen: number | undefined;
    let processIdToOpen: number | undefined;
    switch (source) {
      case 'new':
        key = GridViewGetFormIDByTabID.OPEN_NEW;
        break;
      case 'copy':
        key = GridViewGetFormIDByTabID.COPY_DATA;
        break;
      case 'export':
        key = GridViewGetFormIDByTabID.EXPORT_DATA;
        break;
      case 'refresh':
        key = GridViewGetFormIDByTabID.REFRESH_DATA;
        break;
      case 'delete':
        key = GridViewGetFormIDByTabID.DELETE_DATA;
        break;
    }

    //? Maybe we should reconsider the way we reassign the formIdToOpen and processIdToOpen parameters
    //? It's generally a bad practice to reassign parameters
    formIdToOpen = this.#config.getConstant(key + this.tabId);
    processIdToOpen = this.#config.getConstant(key + this.tabId);
    if (formIdToOpen === undefined && processIdToOpen === undefined) {
      formIdToOpen = this.#config.getConstant(key + ad_window_id);
      processIdToOpen = this.#config.getConstant(key + ad_window_id);
    }
    return { formIdToOpen, processIdToOpen };
  }

  /**
   * mise � jour du readOnly
   * @param dataStore nouveau datastore � prendre en compte
   */
  updateReadOnlyLogic(dataStore?: DataStore) {
    let isReadOnly = false;
    const readOnlyLogic = this.data.readOnlyLogic;
    if (this.data.isReadOnly) {
      isReadOnly = true;
    } else if (readOnlyLogic) {
      isReadOnly = EditViewUtils.checkLogic(readOnlyLogic, this.getCurrentContext(dataStore));
    }
    this.isReadOnly = isReadOnly;
    if (this.parentTab) {
      this.parentTab.isReadOnly = this.isReadOnly;
    }
    if (!this.isTabTopLevel) {
      this.menuBarUiComponent.isReadOnly = this.isReadOnly;
      this.menuBarUiComponent.isInsertRecord = this.isReadOnly ? false : this.data.isInsertRecord;
      this.menuBarUiComponent.updateButtonLists();
    }
  }

  /**
   * mise � jour du IsDeletable
   * @param dataStore nouveau datastore � prendre en compte
   */
  updateIsDeleteableOnlyLogic(dataStore?: DataStore) {
    if (this.parentTab && this.parentTab.isDeleteable === false) {
      this.isDeleteable = false;
      return;
    }

    this.isDeleteable = this.data.isDeletableLogic
      ? EditViewUtils.checkLogic(this.data.isDeletableLogic, this.getCurrentContext(dataStore))
      : true;
  }

  updateAllLogic(datastore?: DataStore) {
    this.updateDisplayLogic(datastore);
    this.updateReadOnlyLogic(datastore);
    this.updateIsDeleteableOnlyLogic(datastore);
  }

  getTabWhereclause(validation = null) {
    if (this.initGridParams?.request?.validation) {
      validation = validation
        ? validation + ' and ' + this.initGridParams?.request?.validation
        : this.initGridParams?.request?.validation;
    }

    if (!validation && this.data.validationCode) {
      validation = LogicEvaluator.replaceVariables(
        this.data.validationCode,
        this.#connectorService.getIupicsUserContext(),
        this.getCurrentContext()
      );
    }

    if (this.gridTabValidator && this.gridTabValidator.trim().length > 0) {
      validation = `${validation ? validation + ' and ' : ''}${this.gridTabValidator}`;
    }

    validation = LogicEvaluator.replaceVariables(
      validation,
      this.#connectorService.getIupicsUserContext(),
      this.getCurrentContext()
    );

    return validation;
  }

  executeProcessWrapper: (paramsMap: ProcessRequest, channel_id: string, self: ProcessUiComponent) => void = (
    paramsMap,
    channel_id,
    self
  ) => {
    if (this.isMultiProcessOpen) {
      // only in grid mode
      const exportIds = this.GridTabInfinityScrollUiComponent.getExportSelections();
      const paramsMaps = [];
      for (const exportId of exportIds) {
        const keys = Object.keys(exportId);
        const newParamsMap = cloneDeep(paramsMap);
        newParamsMap.record_id = exportId[keys[0]] as number; // we take the first column as record_id
        for (const key of keys) {
          // we store the key columns in the windowCtx
          newParamsMap.windowCtx[key] = exportId[key];
        }
        newParamsMap.params['channel_id'] = self.newChannelId();
        paramsMaps.push(newParamsMap);
      }

      this.#processService.executeProcess(...paramsMaps).subscribe((responses) => {
        this.updateModalDisplay(ModalDisplayRequest.Process(false));
        console.log(responses);
      });
    } else {
      self.executeProcess(paramsMap, channel_id, self);
    }
  };
}
